'use client';

import {
	useContext,
	type MouseEventHandler,
	type KeyboardEventHandler,
} from 'react';
import cn from 'classnames';
import { InteractiveObjectLinkContext } from '../../../interactive-object-links-rich-text/context';
import type {
	PortableTextMarkComponentProps,
	PortableTextMarkComponent,
} from '@portabletext/react';
import type { SanityInteractiveObjectLink } from '@metmuseum/types';
import styles from './interactive-object-link.module.scss';

export const InteractiveObjectLink: PortableTextMarkComponent = ({
	children,
	value,
}: InteractiveObjectLinkProps) => {
	const context = useContext(InteractiveObjectLinkContext);
	if (!context) {
		throw new Error(
			'useInteractiveObjectLink must be used within InteractiveObjectLinksRichText',
		);
	}
	const { activeKey, setActiveKey } = context;
	const isActive = activeKey === value._key;

	const handleClick: MouseEventHandler = () => setActiveKey(value._key);
	const handleKeyDown: KeyboardEventHandler = (e) => {
		if (e.key === 'Enter') {
			setActiveKey(value._key);
		}
	};

	return (
		<span
			className={cn(styles.link, { [styles.active]: isActive })}
			onClick={handleClick}
			role="button"
			aria-pressed={isActive}
			data-testid="interactive-object-link-btn"
			tabIndex={0}
			onKeyDown={handleKeyDown}
		>
			{children}
		</span>
	);
};

export interface InteractiveObjectLinkProps extends PortableTextMarkComponentProps {
	value: SanityInteractiveObjectLink;
}
